import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Panel, People, Stat, Title, ago, uxd } from "@/components/admin/kit";

// Everything at a glance, refreshed every 15 seconds. An expired session goes back to the login; any other failure
// says so instead of leaving old numbers up as if they were live.
export function StatsPanel({ onOut }) {
  const [s, setS] = useState(null);
  const [at, setAt] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let live = true;
    const load = () => api("/admin/stats")
      .then((d) => { if (live) { setS(d); setAt(new Date()); setErr(null); } })
      .catch((e) => { if (live) (e.status === 401 ? onOut() : setErr(e.message)); });
    load();
    const t = setInterval(load, 15000);
    return () => { live = false; clearInterval(t); };
  }, [onOut]);
  if (err && !s) return <div className="text-rose-600" data-testid="stats-error">Couldn't load stats: {err}</div>;
  if (!s) return <div className="text-slate-500" data-testid="stats-loading">Loading stats…</div>;
  const { people, ground, signups, economy, activity, shares } = s;
  return (
    <div className="space-y-5" data-testid="stats-panel">
      <div className={`flex items-center gap-2 text-xs ${err ? "text-rose-600" : "text-slate-500"}`} data-testid="stats-status">
        <RefreshCw size={12} className={err ? "" : "animate-spin [animation-duration:3s]"} />
        {err ? `Not refreshing: ${err} · last updated ${at?.toLocaleTimeString()}` : `Live · updated ${at?.toLocaleTimeString()}`}
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Lights on" value={people.lit} sub={`${people.dark} dark · ${people.hidden} hidden`} testId="stat-lit" />
        <Stat label="Sign-ups" value={signups.day} sub={`last 24h · ${signups.week} this week`} testId="stat-signups" />
        <Stat label="Ground spots" value={`${ground.taken}/${ground.capacity}`} sub={`${people.floors} floors built on top`} testId="stat-ground" />
        <Stat label="Uptown" value={people.uptown} sub={`${people.lit - people.uptown} on the Docks`} testId="stat-uptown" />
        <Stat label="UXD in circulation" value={economy.held.toLocaleString()} sub="cash held by residents" testId="stat-held" />
        <Stat label="Invested" value={economy.invested.toLocaleString()} sub={`${economy.invested_day.toLocaleString()} UXD in the last 24h`} testId="stat-invested" />
        <Stat label="Created" value={economy.created.toLocaleString()} sub="UXD paid out, last 24h" testId="stat-created" />
        <Stat label="Removed" value={economy.removed.toLocaleString()} sub="UXD taken back, last 24h" testId="stat-removed" />
        <Stat label="Visits today" value={activity.visits} sub="docks visited today" testId="stat-visits" />
        <Stat label="Duels" value={activity.duels} sub="last 24h" testId="stat-duels" />
        <Stat label="Docks with ships" value={activity.ships} sub="at least one ship out" testId="stat-ships" />
        <Stat label="Mayor candidates" value={activity.candidates} sub="in this week's race" testId="stat-candidates" />
        <Stat label="Share clicks" value={shares.opens.day} sub={`last 24h · ${shares.opens.total} all time · ${shares.previews.total} X previews`} testId="stat-shares" />
        <Stat label="Latest Ledger" value={s.paper ? `No. ${s.paper.issue}` : "–"} sub={s.paper ? ago(s.paper.published_at) : "not published yet"} testId="stat-paper" />
      </div>
      <div className="grid gap-5 md:grid-cols-3">
        <Panel><Title>Richest</Title><People rows={s.top} right={(r) => uxd(r.worth)} testId="stats-top" /></Panel>
        <Panel><Title>Tallest buildings</Title><People rows={s.tallest} right={(r) => `${r.size} floors`} testId="stats-tallest" /></Panel>
        <Panel><Title>Just moved in</Title><People rows={s.recent} right={(r) => ago(r.joined)} testId="stats-recent" /></Panel>
      </div>
    </div>
  );
}
